// iter162 — Help & Support screen.
// iter196 — Contact email + phone are now fetched dynamically from
// `/api/app-config/help-contact`. The backend seeds sensible defaults so
// the screen always has something to show even on a fresh install.
// iter197 — Removed any local cache: state starts empty, effect refetches
// on every mount (including remounts on tab-return), and the request is
// forced fresh with a timestamp cache-buster + `Cache-Control: no-cache`
// headers so browser / service-worker / proxy caches can never serve a
// stale value.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, LifeBuoy, Mail, Phone } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HelpScreen = () => {
  const navigate = useNavigate();
  // Start empty — no client-side fallback that could look like cached
  // values while the fetch is in flight. UI shows "…" placeholders
  // until the network response arrives.
  const [contact, setContact] = useState({ email: '', phone: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // Reset state on every mount so a remount never briefly renders the
    // previous mount's values.
    setContact({ email: '', phone: '' });
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/app-config/help-contact`,
          {
            // Cache-buster query param bypasses any HTTP caches keyed on URL.
            params: { _: Date.now() },
            // Belt-and-braces: also declare no-cache in headers so
            // service workers / CDNs honour the request.
            headers: {
              'Cache-Control': 'no-cache',
              Pragma: 'no-cache',
            },
          }
        );
        if (cancelled) return;
        const c = res?.data?.contact || {};
        setContact({
          email: c.email || '',
          phone: c.phone || '',
        });
      } catch (e) {
        // Leave fields empty — UI will show placeholders rather than
        // stale values.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-white pb-24" data-testid="help-screen">
      <div className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="flex items-center gap-3 px-4 py-3 max-w-2xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="help-back-btn"
            aria-label="Back"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex items-center gap-2">
            <LifeBuoy className="w-5 h-5 text-[#1B2A7B]" />
            <h1 className="text-base font-semibold text-gray-900">Help &amp; Support</h1>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-2xl bg-[#EEF1FC] flex items-center justify-center mx-auto mb-5">
            <LifeBuoy className="w-10 h-10 text-[#1B2A7B]" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">We're here to help</h2>
          <p className="text-sm text-gray-600 leading-relaxed max-w-md mx-auto">
            Reach out any time — our support team responds within a few hours on
            working days.
          </p>
        </div>

        <div className="space-y-3" data-testid="help-contact-list">
          <a
            href={`mailto:${contact.email}`}
            className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white px-4 py-3.5 hover:border-[#1B2A7B] hover:bg-[#F7F9FF] transition-colors"
            data-testid="help-contact-email"
          >
            <div className="w-10 h-10 rounded-lg bg-[#EEF1FC] flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-[#1B2A7B]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs uppercase tracking-wide text-gray-500">Email us</div>
              <div className="text-sm font-medium text-gray-900 truncate">
                {loading ? '…' : contact.email}
              </div>
            </div>
          </a>

          <a
            href={`tel:${contact.phone.replace(/\s+/g, '')}`}
            className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white px-4 py-3.5 hover:border-[#1B2A7B] hover:bg-[#F7F9FF] transition-colors"
            data-testid="help-contact-phone"
          >
            <div className="w-10 h-10 rounded-lg bg-[#EEF1FC] flex items-center justify-center flex-shrink-0">
              <Phone className="w-5 h-5 text-[#1B2A7B]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs uppercase tracking-wide text-gray-500">Call us</div>
              <div className="text-sm font-medium text-gray-900 truncate">
                {loading ? '…' : contact.phone}
              </div>
            </div>
          </a>
        </div>
      </div>
    </div>
  );
};

export default HelpScreen;
